import { useEffect } from 'react'
import { isAxiosError } from 'axios'

import { captureAxiosError, logSentry } from '@gto/sentry'

type QueryLike = {
  /**
   * The TanStack Query error. `unknown` because TanStack itself defaults
   * `TError` to `unknown` (when the query doesn't pin a more specific error
   * type), and a query in the success / pre-failure state carries
   * `error: null` — both must flow through this param without a cast.
   */
  error: unknown
  /** TanStack: true once retries are exhausted. */
  isError: boolean
  /** TanStack: true while a fetch is in-flight (including a retry). */
  isFetching: boolean
}

/**
 * Forwards a TanStack Query failure to Sentry on TERMINAL error only — the bare
 * app QueryClient has no global error sink, so each admin page owns observability
 * for its own queries. The effect only fires when the query has stopped retrying
 * (`isError && !isFetching`), so an intermittent transport blip with three
 * retries no longer multiplies Sentry events. Dedup is by `error` REFERENCE
 * identity within the effect's dep array — the same `Error` object firing twice
 * in a row reports once; a distinct `Error` reference reports again.
 *
 * Axios errors go through `captureAxiosError` (keeps request metadata); anything
 * else through `logSentry`.
 */
export const useReportQueryError = (query: QueryLike, logPrefix: string) => {
  const { error, isError, isFetching } = query
  const terminal = isError && !isFetching

  useEffect(
    function reportQueryError() {
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- async query-state arrival, not a user event: this reports a TanStack Query terminal failure to Sentry, and the bare app QueryClient has no global error sink while v5 removed useQuery's onError, so there is no callback home for "retries exhausted". The effect fires on the isError/!isFetching transition and dedupes on the error reference; a handler cannot own a background query's failure.
      if (!terminal || error == null) {
        return
      }
      if (isAxiosError(error)) {
        captureAxiosError(error, 'react')
      } else {
        logSentry(logPrefix, error)
      }
    },
    [terminal, error, logPrefix],
  )
}
